/**
 * Persistent message context attached to each user message.
 *
 * Unlike ephemeral context (see context/providers), the data gathered here is
 * attached to the user message and persisted in the conversation history via
 * the toolkit's `pluginInjectMessageContext`. This gives the model memory of
 * past application state across turns.
 *
 * The layers/catalog sections, the diff/emit/reset lifecycle, and the
 * persisted-baseline envelope are all owned by the toolkit's
 * `createLayeredContextDiffer` (RFC-0004 §4.8, RFC-0013 §5.5) — this module
 * only supplies its own string sections (app, camera, basemap, location,
 * membership, route, selectedFeature, userPlatform).
 *
 * @module context/message
 */
import {
  createLayeredContextDiffer,
  getActiveLayerIds,
  getDateTimeContext,
  getLayerIndex,
  getMembershipContext,
  getUserPlatformContext,
  type LayeredMessageSnapshot,
} from '@onxmaps/ai-toolkit/toolbox'
import { camera as getCamera } from '@/services/map-service'
import { basemap as getBasemap } from '@/services/basemap-service'
import type { AiDependencies } from '@/lib/ai/lib/dependencies'
import { getActiveRouteContext } from '@/lib/ai/context/providers/get-active-route-context'
import { getLocationContext } from '@/lib/ai/context/providers/get-location-context'
import { getSelectedFeatureContext } from '@/lib/ai/context/providers/get-selected-feature-context'
import { formatCamera } from '@/lib/ai/context/providers/format-camera'
import browser from '@/lib/browser'

/**
 * Pre-formatted, single-line context sections diffed by string equality. Each
 * value already carries its own label (e.g. `basemap: aerial`); an empty
 * string means the section is absent this turn. Ordered so emitted changes
 * read consistently. Layers and the layer catalog are the toolkit differ's
 * own sections, not listed here.
 */
type StringSection =
  | 'app'
  | 'camera'
  | 'basemap'
  | 'location'
  | 'membership'
  | 'route'
  | 'selectedFeature'
  | 'userPlatform'

/** Sections diffed by string equality, in emission order. */
const STRING_SECTIONS: readonly StringSection[] = [
  'app',
  'camera',
  'basemap',
  'location',
  'membership',
  'route',
  'selectedFeature',
  'userPlatform',
]

/** Schema version for the persisted snapshot below. Bump on a shape change. */
const SNAPSHOT_SCHEMA_VERSION = 1

/**
 * Diffs successive snapshots into the text attached to a user message. Held
 * in module scope because the agent (and therefore this plugin) is cached for
 * the app's lifetime and reused across conversations. `resetMessageContext`
 * clears the baseline so each new conversation re-establishes full state on
 * its first message.
 *
 * The baseline is persisted alongside the conversation via
 * {@link snapshotMessageContext}/{@link hydrateMessageContext}, below, so a
 * conversation restored from storage resumes this differ from the same
 * baseline instead of re-emitting a spurious first-message turn.
 */
const differ = createLayeredContextDiffer<StringSection>({
  sections: STRING_SECTIONS,
  header: (isFirst) => {
    return isFirst
      ? "user's current context:"
      : "user's current context (changes since the previous message):"
  },
  prefix: () => getDateTimeContext().trimEnd(),
  version: SNAPSHOT_SCHEMA_VERSION,
})

/**
 * Clears the diff baseline so the next message emits full state again. Call
 * this whenever a new conversation begins.
 */
export const resetMessageContext = (): void => {
  differ.reset()
}

/**
 * Serializes the differ's current baseline for persistence alongside the
 * conversation, or `undefined` when there's no baseline yet (before the first
 * message, or after {@link resetMessageContext}).
 */
export const snapshotMessageContext = (): string | undefined =>
  differ.serialize()

/**
 * Restores a baseline previously captured by {@link snapshotMessageContext},
 * or clears the baseline (equivalent to {@link resetMessageContext}) when
 * `raw` is `null`, `undefined`, unparsable, or fails the shape check. Never
 * throws: the toolkit's `LayeredContextDiffer.deserialize` treats malformed or
 * incompatible stored input as "no baseline" rather than breaking the caller.
 */
export const hydrateMessageContext = (raw: string | null | undefined): void => {
  differ.deserialize(raw)
}

/**
 * Snapshots the current map/app state (camera, basemap, location, membership,
 * active route, selected feature, active layers) and user platform (device,
 * browser), and emits only what changed since the previous message in this
 * conversation. The toolkit's `pluginInjectMessageContext` attaches the
 * returned text to the user message so it persists in conversation history;
 * an empty string means nothing changed and no context is attached this turn.
 *
 * Each changed key is reported under its own label as a readable line — never
 * a stringified JSON blob — and the layer diff emits the layers that turned
 * on/off. The first message of a conversation reports everything, establishing
 * the baseline.
 *
 * The layer catalog — a compact id/name index of toggleable layers
 * (descriptions are intentionally omitted to keep responses lean) — is
 * withheld from the first message since it isn't always needed, introduced on
 * the second, then re-sent only when the available set changes.
 */
export const getMessageContext = async (
  deps: AiDependencies
): Promise<string> => {
  const camera = getCamera()
  const basemap = getBasemap()

  // Location needs an async lookup. Fetched every turn — including the
  // first — so location is available from message one.
  const location = await getLocationContext(deps.viewer)
  const locationText = location.text.trimEnd()

  const layers = new Set(getActiveLayerIds(deps.layers))
  const catalog = getLayerIndex(deps.layers)

  const snapshot: LayeredMessageSnapshot<StringSection> = {
    app: `using ${deps.identity.productLabel}`,
    camera: camera ? formatCamera(camera) : '',
    basemap: basemap ? `basemap: ${basemap}` : '',
    location: locationText,
    membership: getMembershipContext(deps.membership).trimEnd(),
    route: getActiveRouteContext().trimEnd(),
    selectedFeature: getSelectedFeatureContext().trimEnd(),
    userPlatform: getUserPlatformContext(browser),
    layers,
    catalog,
  }

  const text = differ.diff(snapshot)
  return text ? `${text}\n` : ''
}
